import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetAgentContractByIDQuery } from '@/apis/extended/agentApi';
import {
  useAddSellerContractByIDMutation,
  useGetAllSellerContractsQuery,
} from '@/apis/extended/sellerAPI';
import { Loader } from '@/components/Layout/Loader';
import { ProductContractSummary } from '@/components/Layout/SellerAgent/Negotiation/ProductContractSummary';
import { VoidContractButton } from '@/components/Negotiation/VoidContractButton';
import {
  getContractAddDetailsRoute,
  getContractNegotiationRoute,
  getContractViewRoute,
  getCurrentUserRole,
} from '@/utils/contractRouting';
import theme from '@/utils/theme';
import { AgentProductCard } from '../SellerAgent/Product/AgentProductCard';
import { SellerPropertyIdModal } from './Product/propertyId';

const SellerMainPage = () => {
  const { sellerId } = useParams();
  const [inputId, setInputId] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const [addSellerContractByID] = useAddSellerContractByIDMutation();
  const {
    data: sellerContracts,
    refetch,
    isLoading: isContractLoading,
  } = useGetAllSellerContractsQuery(sellerId ?? '', {
    skip: !sellerId,
  });

  const verifyPropertyId = async () => {
    if (!sellerId || !inputId) {
      setError('Missing seller ID or contract ID');
      return;
    }
    setIsLoading(true);
    const payload = {
      sellerId: sellerId ?? '',
      contractId: inputId,
    };
    try {
      await addSellerContractByID(payload).unwrap();
      setInputId('');
      setError('');
      await refetch();
    } catch (error) {
      setError(
        'Failed to add contract. Please check the contract ID and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };
  if (isContractLoading) {
    return <Loader isFull />;
  }

  if (!sellerId || !sellerContracts || sellerContracts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Box sx={{ maxWidth: '500px', width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            No Contracts Found
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: theme.palette.greyShades.grey1 }}
          >
            You don't have any contracts yet. Please enter a Contract ID to
            start your first contract.
          </Typography>
          <TextField
            label="Contract ID"
            fullWidth
            size="small"
            value={inputId}
            onChange={(e) => {
              setInputId(e.target.value);
              setError('');
            }}
            helperText={error}
            error={!!error}
            disabled={isLoading}
            sx={{ mb: 2 }}
            placeholder="Enter contract ID here..."
          />
          <Button
            variant="contained"
            fullWidth
            onClick={verifyPropertyId}
            disabled={isLoading}
            sx={{ mb: 2 }}
          >
            {isLoading ? <Loader2 className="animate-spin" /> : 'Add Contract'}
          </Button>
        </Box>
      </Box>
    );
  } else {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 4,
          }}
        >
          {Array.isArray(sellerContracts) &&
            sellerContracts.map((item: any) => {
              const isRatified =
                item.contractDetails.contractStatus === 'ratified' ||
                item.contractDetails.isRatified === true;
              const isVoided = item.contractDetails.contractStatus === 'voided';

              return (
                <Box
                  key={item._id}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    gap: '32px',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    width: '100%',
                    maxWidth: '1200px',
                  }}
                >
                  {/* Product Card */}
                  <Box sx={{ flex: '1', maxWidth: '500px', width: '100%' }}>
                    <AgentProductCard
                      product={item.propertyDetails}
                      navigateTo={`/seller/product/${item.propertyDetails?._id}`}
                    />
                  </Box>

                  {/* Contract Summary with Action Buttons */}
                  <Box
                    sx={{ flex: '0 0 400px', maxWidth: '400px', width: '100%' }}
                  >
                    {/* ProductContractSummary has status badge in header + optional reject button */}
                    <ProductContractSummary
                      contractId={item.contractDetails._id}
                      userRole="seller"
                      contractStatus={item.contractDetails.contractStatus}
                      isRatified={item.contractDetails.isRatified}
                      showRejectButton={!isRatified && !isVoided}
                      onRejectSuccess={() => refetch()}
                    />
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        justifyContent: 'center',
                      }}
                    >
                      {/* Contract Status-Based Button Logic */}
                      {isRatified ? (
                        <>
                          {/* Ratified Contract Actions - NO REJECT BUTTON */}
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<i className="fa-light fa-chart-line" />}
                            onClick={() => {
                              navigate(
                                `/seller/property-dashboard/${item.contractDetails._id}`
                              );
                            }}
                          >
                            Sale Dashboard
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<i className="fa-light fa-eye" />}
                            onClick={() => {
                              navigate(
                                `/view-contract/${item.contractDetails._id}`
                              );
                            }}
                          >
                            Review Contract
                          </Button>
                        </>
                      ) : isVoided ? (
                        <>
                          {/* Voided Contract Display - NO REJECT BUTTON */}
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: '#fee2e2',
                              borderRadius: '8px',
                              border: '1px solid #dc2626',
                              textAlign: 'center',
                              width: '100%',
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: '#991b1b', fontWeight: 600 }}
                            >
                              This contract has been rejected
                            </Typography>
                            {item.contractDetails.voidedAt && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#991b1b',
                                  display: 'block',
                                  mt: 0.5,
                                }}
                              >
                                Voided on{' '}
                                {new Date(
                                  item.contractDetails.voidedAt
                                ).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                          <Button
                            variant="outlined"
                            startIcon={<i className="fa-light fa-eye" />}
                            onClick={() => {
                              navigate(
                                `/view-contract/${item.contractDetails._id}`
                              );
                            }}
                          >
                            View Contract
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Active Contract Actions - Main buttons WITHOUT Reject */}

                          {/* Show Sign Contract button if contract is ready for signature */}
                          {(item.contractDetails.contractStatus ===
                            'awaiting_signatures' ||
                            item.contractDetails.contractStatus ===
                              'awaiting_seller_signature') &&
                            !item.contractDetails.sellerSign?.isStored && (
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={
                                  <i className="fa-light fa-file-signature" />
                                }
                                onClick={() => {
                                  navigate(
                                    `/seller/contract/${item.contractDetails._id}/sign`
                                  );
                                }}
                                sx={{ fontWeight: 600 }}
                              >
                                Sign Contract
                              </Button>
                            )}

                          <Button
                            variant="outlined"
                            startIcon={<i className="fa-light fa-eye" />}
                            onClick={() => {
                              const contractRoute = getContractViewRoute({
                                contractId: item.contractDetails._id,
                                contractStatus:
                                  item.contractDetails.contractStatus,
                                userRole: 'seller',
                              });
                              navigate(contractRoute);
                            }}
                          >
                            View Contract
                          </Button>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<i className="fa-light fa-handshake" />}
                            onClick={() => {
                              const negotiateRoute =
                                getContractNegotiationRoute(
                                  item.contractDetails._id,
                                  'seller'
                                );
                              navigate(negotiateRoute);
                            }}
                          >
                            Negotiate
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<i className="fa-light fa-plus" />}
                            onClick={() => {
                              const addDetailsRoute =
                                getContractAddDetailsRoute(
                                  item.contractDetails._id,
                                  'seller'
                                );
                              navigate(addDetailsRoute);
                            }}
                          >
                            Add Details
                          </Button>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
        </Box>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setIsModalOpen(true)}
            startIcon={<i className="fa-light fa-plus" />}
          >
            Add Another Contract
          </Button>
        </Box>
        <SellerPropertyIdModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          sellerId={sellerId}
        />
      </>
    );
  }
};

export default SellerMainPage;
